/**
 * Chybový stav. Ukazuje českou hlášku z backendu, ne „Something went wrong".
 */
import { ApiError } from '@/api/client';

export interface ErrorStateProps {
  error: unknown;
  onRetry?: () => void;
  title?: string;
}

function describe(error: unknown): { title: string; detail: string; hint?: string } {
  if (error instanceof ApiError) {
    if (error.isForbidden) {
      return {
        title: 'Nedostatečné oprávnění',
        detail: error.message,
        hint: 'Tuhle akci má k dispozici role analyst nebo admin. Požádej správce o zvýšení role.',
      };
    }
    if (error.isNotFound) {
      return {
        title: 'Nenalezeno',
        detail: error.message,
        hint: 'Zkontroluj odkaz — položka mohla být přejmenovaná nebo smazaná.',
      };
    }
    if (error.status >= 500) {
      return {
        title: 'Chyba na straně serveru',
        detail: error.message,
        hint: 'Zkus to za chvíli znovu. Pokud potíže trvají, mrkni na zdraví ETL v sekci Admin.',
      };
    }
    return { title: 'Požadavek selhal', detail: error.message };
  }
  if (error instanceof Error) {
    return {
      title: 'Neočekávaná chyba',
      detail: error.message,
      hint: 'Detail je v konzoli prohlížeče.',
    };
  }
  return { title: 'Neočekávaná chyba', detail: String(error) };
}

export function ErrorState({ error, onRetry, title }: ErrorStateProps) {
  const info = describe(error);
  return (
    <div className="error-state" role="alert">
      <div className="error-state__icon" aria-hidden="true">
        <svg viewBox="0 0 24 24" width="24" height="24" fill="none">
          <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.5" />
          <path d="M12 7.5v5.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          <circle cx="12" cy="16.5" r="1" fill="currentColor" />
        </svg>
      </div>
      <div className="error-state__body">
        <h3 className="error-state__title">{title ?? info.title}</h3>
        <p className="error-state__detail">{info.detail}</p>
        {info.hint ? <p className="error-state__hint">{info.hint}</p> : null}
        {onRetry ? (
          <button type="button" className="btn btn--ghost" onClick={onRetry}>
            Zkusit znovu
          </button>
        ) : null}
      </div>
    </div>
  );
}

/** Selhaný job — spec chce vysvětlení, ne prázdnou obrazovku. */
export function JobFailureState({
  job,
  error,
  onRerun,
}: {
  job: string;
  error: string | null;
  onRerun?: () => void;
}) {
  return (
    <div className="error-state" role="alert">
      <div className="error-state__body">
        <h3 className="error-state__title">Job „{job}" selhal</h3>
        <p className="error-state__detail">{error ?? 'Job neuvedl důvod selhání.'}</p>
        <p className="error-state__hint">
          Data zobrazená níže mohou být zastaralá — pocházejí z posledního úspěšného běhu.
        </p>
        {onRerun ? (
          <button type="button" className="btn btn--ghost" onClick={onRerun}>
            Spustit znovu
          </button>
        ) : null}
      </div>
    </div>
  );
}
